import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { TranslocoService } from '@ngneat/transloco'
import { NotifyService } from './notifications/notify.service'
import { ApiService } from './api.service'
import { AuthService } from './auth.service'
import { signInWithCustomToken } from 'firebase/auth'
import { FirestoreService2 } from './firestore.service2'
import { GlobalService } from './global.service'
import { use } from './utils'

let env: any
let BUILD: any

@Injectable({
  providedIn: 'root'
})
export class AuthMsadService {

  private fss: FirestoreService2

  constructor(
    private auth: AuthService,
    private http: HttpClient,
    private router: Router,
    private notify: NotifyService,
    private g: GlobalService,
    private transloco: TranslocoService) {
    console.info(`## ${this.constructor.name}`)
    env = (window as any).y4wenvs.ENV
    BUILD = (window as any).y4wenvs.BUILD
  }

  /**
   * Method to perform Microsoft login on Android using the plugin we created for Capacitor
   * @param {string} token
   * @returns {Promise<any>}
   */
  microsoftLoginViaCapacitorPlugin(token: string): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.toPromise(this.http.get('https://graph.microsoft.com/v1.0/me', {
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    })).then((user: any) => {
      if (!user || !user.mail || user.mail.toLowerCase().indexOf('.onmicrosoft.com') > -1) {
        this.saveDebug({ message: `# adUser email invalido`, stack: JSON.stringify(user) })
        this.notify.update('O teu email não é valido, por favor contacte o HelpDesk', 'btn-danger', 6000)
        return Promise.reject('email_is_invalid')
      }
      return this.authMicrosoftWithFirebase(user)
    }).catch(async er => {
      console.log('# graph', er.message)
      this.saveDebug({ message: `# microsoftLoginViaCapacitorPlugin`, stack: er.message })
      this.notify.alert('Atenção!', er.message)
    })
  }

  /**
   * Listens for Microsoft login for web and iOS
   * @param {string} swapId
   * @returns {Promise<unknown>}
   */
  listenMicrosoftLogin(swapId: string = null) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    this.saveDebug({ message: `# listenMicrosoftLogin, [${swapId}]` })
    return new Promise((resolve, reject) => {
      const listen = this.fss.get('__swap', swapId).subscribe(swap => {
        if (swap.access_token && !swap.used) {
          this.fss.toPromise(this.http.get('https://graph.microsoft.com/v1.0/me', {
            headers: {
              'Authorization': swap.access_token,
              'Content-Type': 'application/json'
            }
          })).then((adUser: any) => {
            if (!adUser || !adUser.mail || adUser.mail.toLowerCase().indexOf('.onmicrosoft.com') > -1) {
              this.saveDebug({ message: `# adUser email invalido, [${swapId}]`, stack: JSON.stringify(adUser) })
              this.notify.update('O teu email não é valido, por favor contacte o HelpDesk', 'btn-danger', 6000)
              return
            }
            this.saveDebug({ message: `# adUser, [${swapId}]`, stack: JSON.stringify(adUser) })
            this.authMicrosoftWithFirebase(adUser).then(_user => resolve(_user)).catch(_er => reject(_er))
            this.fss.merge('__swap', swapId, { used: true }, { silent: true }).then(() => {
              this.fss.delete('__swap', swapId).then()
            })
          }).catch(async er => {
            this.notify.update(er.message, 'btn-danger', 3000)
            reject(er)
          })
          listen.unsubscribe()
        }
      })
    })
  }

  /**
   * Authenticates in FirebaseAuth using the user that came from Microsoft AD
   * @param adUser
   * @returns {Promise<unknown>}
   */
  authMicrosoftWithFirebase(adUser: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api: ApiService = use<ApiService>(ApiService)
    return new Promise((resolve, reject) => {
      this.fss.toPromise(api.post('users/userAction?noAuthRequest=true', {
        action: 'validateToken',
        uid: adUser.uid || adUser.id,
        email: adUser.mail || adUser.givenName
      }, true)).then(ok => {
        if (ok) {
          if (ok.success) {
            this.saveDebug({ message: `# validateToken `, stack: ok.token })
            this.authWithMicrosoftCredential(ok.token, adUser)
              .then((user: any) => {
                if (!user.error) {
                  this.saveDebug({ message: `# authWithMicrosoftCredential `, stack: JSON.stringify(user) })
                  this.fss.toPromise(api.post('users/updateUserAuth', { uid: user.uid, displayName: adUser.displayName, email: /*'msal__' + */adUser.mail }, true))
                    .then(ok => !env.production && console.log('#updateUserAuth OK', ok)).catch(_er => console.log('#updateUserAuth', _er.message))
                  user.firstLogin = user.firstLogin || new Date().toISOString()
                  user.lastLogin = new Date().toISOString()
                  if (user.id) this.fss.update('users', user.id, { createdAt: user.createdAt, firstLogin: user.firstLogin, lastLogin: user.lastLogin }, { silent: true }).then()
                  resolve(user)
                } else {
                  this.saveDebug({ message: `# authMicrosoftWithFirebase `, stack: JSON.stringify(user) })
                  this.notify.update(this.transloco.translate(user.error), 'btn-danger', 6000)
                  resolve(user)
                }

              })
              .catch(async (error) => {
                this.notify.update(error.message, 'btn-danger', 3000)
                reject(error)
              })
          }

          // ERror
          if (ok.error) {
            this.notify.update(this.transloco.translate(ok.error), 'btn-danger', 6000)
            resolve(ok)
          }
        }
      }).catch(err => {
        this.saveDebug({ ...err, message: `# validateToken ERROR ` + err.message })
        reject(err)
      })
    })
  }

  signInWithCustomToken(token) {
    return signInWithCustomToken(this.auth.afAuth, token)
  }

  /**
   * Creates a user for Microsoft login (if not exists) by checking the prospects
   * @param {string} token
   * @param adUser
   * @returns {Promise<{error: string} | firebase.User | void>}
   */
  authWithMicrosoftCredential(token: string, adUser: any) {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    const api = use<ApiService>(ApiService)
    return signInWithCustomToken(this.auth.afAuth, token)
      .then(async (result) => {
        let msUser: any = null
        const dbUser = await this.fss.toPromise(this.fss.get('users', result.user.uid, { api: true }))
        const prospects: any[] = await this.fss.toPromise(api.post('db/action', { action: 'list', collection: 'prospects', nick: this.g.nick, queryArray: [['used', '==', false]] }, true))
        const prospect = prospects.find(p => p && p.email && p.email.toLowerCase() === adUser.mail.toLowerCase().replace('@some.where', '@ageas.pt')) || null

        if (!dbUser) {
          msUser = {
            id: result.user.uid,
            uid: result.user.uid,
            displayName: adUser.displayName || '',
            name: adUser.displayName || '',
            firstName: (adUser.displayName || '').split(' ')[0],
            email: adUser.mail.toLowerCase(),
            emailVerified: result.user.emailVerified || true,
            isAnonymous: result.user.isAnonymous || false,
            photo: result.user.photoURL || null,
            cart: [],
            cartEuros: [],
            wishlist: [],
            addresses: [],
            balance: 0,
            balanceEuros: 0,
            nick: this.g.nick,
            active: true,
            role: 'user',
            segment: null,
            provider: 'microsoft',
            providerId: adUser.id || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            firstLogin: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
          }

          if (this.g.program.logins.lookup) {
            if (prospect && prospect.active && !prospect.used) {
              msUser.segment = prospect.segment || null
              msUser.segments = [...(prospect.segments || []), ...this.getNewDefaultSegmentIds(prospect.segments || [])]
              msUser.displayName = msUser.displayName || prospect.displayName || ''
              msUser.role = prospect.role || 'user'

              // creates the user via cripted API
              await this.fss.toPromise(api.post('db/action', {
                action: 'createUser',
                id: result.user.uid,
                nick: this.g.nick,
                data: msUser
              }, true))
              this.fss.merge('prospects', prospect.id, { used: true })
            } else return { error: 'user_not_allowed_or_has_registed' }
          } else {
            const defaultIds = this.getNewDefaultSegmentIds([])
            if (defaultIds.length) msUser.segments = defaultIds
            await this.fss.toPromise(api.post('db/action', {
              action: 'createUser',
              id: result.user.uid,
              nick: this.g.nick,
              data: msUser
            }, true))
          }

        } else if (!dbUser.active) {
          this.auth.signOut(false)
          return { error: 'user_not_registed_or_not_activated' }
        }

        const userToUpdate = dbUser || msUser
        if (!userToUpdate) return { error: 'unexpected_null_user' }

        const updateData: any = { createdAt: result.user.metadata?.creationTime || undefined, lastLogin: new Date().toISOString() }

        // Merge default segments (only IDs not already present)
        const newDefaultIds = this.getNewDefaultSegmentIds(userToUpdate.segments)
        if (newDefaultIds.length) {
          userToUpdate.segments = [...(userToUpdate.segments || []), ...newDefaultIds]
          updateData.segments = userToUpdate.segments
        }

        this.fss.update('users', result.user.uid, updateData, { silent: true }).then()

        await this.updateUserSegment(userToUpdate, adUser)

        return result.user
      })
      .catch((error) => {
        this.saveDebug({ message: `# authWithMicrosoftCredential `, stack: error })
        console.log('#error', error)
      })
  }

  saveDebug(error: any) {
    const user = this.g.get('user')
    const err = {
      error: {
        message: error.message,
        stack: error.stack || '',
        name: error.name || ''
      },
      path: window.location.href,
      user: user ? user.email : 'indefinido',
      nick: this.g.nick,
      timestamp: new Date().getTime(),
      createdAt: new Date().toISOString(),
      build: BUILD
    }
    const api = use<ApiService>(ApiService)
    this.fss.toPromise(api.post(`users/userAction?noAuthRequest`, { action: 'debug', err }, true)).catch(() => { })
  }

  //////// API NOSS@GENTE ////////

  getConfig(): Promise<any> {
    const api = use<ApiService>(ApiService)
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.toPromise(api.post('open/firestoreAction', {
      action: 'msadConfig',
      nick: this.g.nick,
    }, true))
  }

  getToken(code: string): Promise<any> {
    const api = use<ApiService>(ApiService)
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.toPromise(api.post('msad/msadAction', {
      action: 'requestADTokenV2',
      code,
      nick: this.g.nick
    }, true))
  }

  getAdUser(token: string) {
    const api = use<ApiService>(ApiService)
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    return this.fss.toPromise(api.post('msad/msadAction', { action: 'requestADUserV2', token, nick: this.g.nick }, true)).then((ok: any) => {
      if (ok) {
        if (ok.success) {
          const adUser = {
            ...ok.data,
            uid: btoa(ok.data.id + ok.data.email).replace(/=/g, ''),
            mail: ok.data.email,
            displayName: ok.data.name || '',
          }

          return this.authMicrosoftWithFirebase(adUser)
        }

        // ERROR
        if (ok.error) {
          this.notify.update(ok.error, 'btn-danger', 4000)
          return ok
        }
      } else return ({ success: false, error: 'unknow error' })
    }).catch(e => console.error(e.message))
  }

  /**
   * Returns the IDs from program.segmentsDefault that are not yet present in user.segments.
   * Does NOT write to Firestore — callers are responsible for persisting the result.
   */
  private getNewDefaultSegmentIds(userSegments: unknown): string[] {
    if (!this.g.program) {
      console.warn('[AuthMsadService] program not loaded — skipping default segments')
      return []
    }
    const segments = Array.isArray(userSegments)
      ? userSegments.filter((s): s is string => typeof s === 'string')
      : []
    const defaults: { id: string, name: string }[] = this.g.program?.segmentsDefault
    if (!Array.isArray(defaults) || !defaults.length) return []
    const existing = new Set(segments)
    return defaults.map(s => s.id).filter(id => id && !existing.has(id))
  }

  private async updateUserSegment(dbUser: any, adUser: any): Promise<any> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    try {
      const _config = await this.getConfig()
      if (!(_config && _config.success && _config.config && _config.config.updateSegment)) {
        return null
      }
      // The code from here on is related to the NOSSAGENTE program and what we receive from their AD!
      if (dbUser.providerId === adUser.id && adUser.commercialStructures && adUser.commercialStructures[0] && adUser.commercialStructures[0].level && adUser.commercialStructures[0].level.type) {
        if (adUser.networks && adUser.networks[0]) {
          let id = null, uniq = null, parentId = null, parentUniq = null, name = null
          if (adUser.commercialStructures[0].level.type === 'Network') {
            id = adUser.networks[0].code
            uniq = adUser.networks[0].code
            parentId = null
            parentUniq = null
            name = adUser.networks[0].name
            await this.prepareSegment(dbUser, { id, parentId, parentUniq, name, uniq })
          }
          if (adUser.commercialStructures[0].level.type === 'Zone') {
            id = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}`
            uniq = adUser.networks[0].zones[0].code
            parentId = adUser.networks[0].code
            parentUniq = adUser.networks[0].code
            name = adUser.networks[0].zones[0].name
            await this.prepareSegment(dbUser, { id, parentId, parentUniq, name, uniq })
          }
          if (adUser.commercialStructures[0].level.type === 'Branch') {
            id = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}_${adUser.networks[0].zones[0].branches[0].workArea}`
            uniq = adUser.networks[0].zones[0].branches[0].workArea
            parentId = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}`
            parentUniq = adUser.networks[0].zones[0].code
            name = adUser.networks[0].zones[0].branches[0].name
            await this.prepareSegment(dbUser, { id, parentId, parentUniq, name, uniq })
          }
          if (adUser.commercialStructures[0].level.type === 'Inspector') {
            id = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}_${adUser.networks[0].zones[0].branches[0].workArea}_${adUser.networks[0].zones[0].branches[0].inspectors[0].code}`
            uniq = adUser.networks[0].zones[0].branches[0].inspectors[0].code
            parentId = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}_${adUser.networks[0].zones[0].branches[0].workArea}`
            parentUniq = adUser.networks[0].zones[0].branches[0].workArea
            name = adUser.networks[0].zones[0].branches[0].inspectors[0].name
            await this.prepareSegment(dbUser, { id, parentId, parentUniq, name, uniq })
          }
          if (adUser.commercialStructures[0].level.type === 'Agent') {
            if (!adUser.classification?.agentClassification?.classifications) {
              this.saveDebug({ message: '# updateUserSegment: Agent sem classification', stack: JSON.stringify(adUser) })
              if (dbUser.segment) {
                dbUser.segment = null
                await this.fss.update('users', dbUser.uid, { segment: null }, { silent: true })
              }
            } else {
              const classification = JSON.stringify(adUser.classification.agentClassification.classifications)
              const inspectorPrefix = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}_${adUser.networks[0].zones[0].branches[0].workArea}_${adUser.networks[0].zones[0].branches[0].inspectors[0].code}`

              const classificationChanged = !dbUser.classifications || dbUser.classifications !== classification
              const segmentMissing = !dbUser.segment
              const parentChainChanged = dbUser.segment && !dbUser.segment.id?.startsWith(inspectorPrefix)

              if (classificationChanged || segmentMissing || parentChainChanged) {
                const segmentsMapping = await this.fss.toPromise(this.fss.list('settings', { subcol: 'segmentsMapping', api: true }))
                const asfNumber = adUser.networks[0].zones[0].branches[0].inspectors[0].agents[0].asfNumber

                if (segmentsMapping.length) {
                  let segmentName = null
                  for (const map of segmentsMapping) {
                    if (!segmentName && adUser.classification.agentClassification.classifications.filter(o => o.code === map.code && o.description === map.description).length) {
                      segmentName = map.segment.toUpperCase()
                      await this.saveProfileSegment(adUser, segmentName)
                    }
                  }

                  dbUser._prevClassifications = dbUser.classifications
                  dbUser.classifications = classification

                  if (segmentName) {
                    const id = `${inspectorPrefix}_${segmentName}_${asfNumber}`
                    const parentId = `${inspectorPrefix}_${segmentName}`
                    const name = adUser.networks[0].zones[0].branches[0].inspectors[0].agents[0].name

                    await this.prepareSegment(dbUser, { id, parentId, parentUniq: segmentName, name, uniq: asfNumber, lowestLevel: true })
                  }
                }
              }
            }
          } // else classification guard
        }
      }
      return true
    } catch (err) {
      console.error('# updateUserSegment error', err)
      return null
    }
  }

  private async prepareSegment(dbUser, segment): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    try {
      const _segment = await this.fss.toPromise(this.fss.getBy('segments', 'uniq', segment.uniq))
      if (_segment) segment.name = _segment.name
      const newSegment = {
        active: true,
        id: segment.id,
        hasRank: true,
        lowestLevel: segment.lowestLevel || false,
        name: segment.name,
        parentId: segment.parentId,
        parentUniq: segment.parentUniq,
        uniq: segment.uniq,
        xrate: 1
      }
      if (!dbUser.segment || dbUser.segment.id !== segment.id) {
        await this.saveUserSegment(dbUser, _segment, newSegment)
      } else if (dbUser.classifications && dbUser._prevClassifications && dbUser.classifications !== dbUser._prevClassifications) {
        await this.fss.update('users', dbUser.uid, { classifications: dbUser.classifications }, { silent: true })
        delete dbUser._prevClassifications
      }
    } catch (err) {
      console.error('# prepareSegment error', err)
    }
  }

  private async saveUserSegment(dbUser: any, segment = null, newSegment): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)
    delete dbUser._prevClassifications
    try {
      if (segment) {
        dbUser.segment = segment
        await this.fss.update('users', dbUser.uid, { segment: dbUser.segment, classifications: dbUser.classifications }, { silent: true })
      } else {
        await this.fss.set('segments', newSegment.id, newSegment, { silent: true })
        dbUser.segment = newSegment
        await this.fss.update('users', dbUser.uid, { segment: dbUser.segment, classifications: dbUser.classifications }, { silent: true })
      }
    } catch (err) {
      console.error('# saveUserSegment error', err)
    }
  }

  private async saveProfileSegment(adUser, segmentName): Promise<void> {
    if (!this.fss) this.fss = use<FirestoreService2>(FirestoreService2)

    const zoneId = `${adUser.networks[0].code}_${adUser.networks[0].zones[0].code}`
    const branchId = `${zoneId}_${adUser.networks[0].zones[0].branches[0].workArea}`
    const inspectorId = `${branchId}_${adUser.networks[0].zones[0].branches[0].inspectors[0].code}`
    const id = `${inspectorId}_${segmentName}`

    try {
      // Zone
      let segment = await this.fss.toPromise(this.fss.get('segments', zoneId))
      if (!segment) {
        await this.fss.set('segments', zoneId, {
          active: true, id: zoneId, hasRank: true,
          name: adUser.networks[0].zones[0].name,
          parentId: adUser.networks[0].code,
          parentUniq: adUser.networks[0].code,
          uniq: adUser.networks[0].zones[0].code,
          xrate: 1
        }, { silent: true })
      }

      // Branch
      segment = await this.fss.toPromise(this.fss.get('segments', branchId))
      if (!segment) {
        await this.fss.set('segments', branchId, {
          active: true, id: branchId, hasRank: true,
          name: adUser.networks[0].zones[0].branches[0].name,
          parentId: zoneId,
          parentUniq: adUser.networks[0].zones[0].code,
          uniq: adUser.networks[0].zones[0].branches[0].workArea,
          xrate: 1
        }, { silent: true })
      }

      // Inspector
      segment = await this.fss.toPromise(this.fss.get('segments', inspectorId))
      if (!segment) {
        await this.fss.set('segments', inspectorId, {
          active: true, id: inspectorId, hasRank: true,
          name: adUser.networks[0].zones[0].branches[0].inspectors[0].name,
          parentId: branchId,
          parentUniq: adUser.networks[0].zones[0].branches[0].workArea,
          uniq: adUser.networks[0].zones[0].branches[0].inspectors[0].code,
          xrate: 1
        }, { silent: true })
      }

      // Agent segment
      segment = await this.fss.toPromise(this.fss.get('segments', id))
      if (!segment) {
        await this.fss.set('segments', id, {
          active: true, id, hasRank: true,
          name: segmentName,
          parentId: inspectorId,
          parentUniq: adUser.networks[0].zones[0].branches[0].inspectors[0].code,
          uniq: segmentName,
          xrate: 1
        }, { silent: true })
      }
    } catch (err) {
      console.error('# saveProfileSegment error', err)
    }
  }
}
